/**
 * Offline rendering of SFX bank entries into one mono sample buffer (pure; runs in Node tests too).
 */
import type { SfxDef } from './sfx-bank';
import { buildZzfxSamples } from './zzfx';

/** Peak the rendered mix is normalised to before the def's `gain` is applied. */
const NORMALISE_PEAK = 1;
/** Fade (s) applied to the tail so a truncated layer can never click. */
const TAIL_FADE = 0.004;

/**
 * Mix every layer of `def` (with its start offset and gain) into one Float32Array, normalise the peak to 1 and
 * scale by `def.gain` (default 0.5). Empty defs render to a zero-length buffer.
 */
export const renderSfx = (def: SfxDef, sampleRate = 44100): Float32Array => {
  const parts = def.layers.map((l) => ({
    start: Math.max(0, Math.round((l.at ?? 0) * sampleRate)),
    gain: l.gain ?? 1,
    samples: buildZzfxSamples(l.params, sampleRate),
  }));
  let length = 0;
  for (const p of parts) length = Math.max(length, p.start + p.samples.length);
  const out = new Float32Array(length);
  for (const p of parts) {
    const s = p.samples;
    for (let i = 0; i < s.length; i++) out[p.start + i]! += (s[i] ?? 0) * p.gain;
  }
  let peak = 0;
  for (let i = 0; i < length; i++) {
    const v = out[i]!;
    if (!Number.isFinite(v)) out[i] = 0;
    else if (Math.abs(v) > peak) peak = Math.abs(v);
  }
  const scale = peak > 0 ? (NORMALISE_PEAK / peak) * (def.gain ?? 0.5) : 0;
  const fade = Math.min(length, Math.round(TAIL_FADE * sampleRate));
  for (let i = 0; i < length; i++) {
    const tail = length - i <= fade ? (length - i) / fade : 1;
    out[i] = out[i]! * scale * tail;
  }
  return out;
};

/** Duration in seconds of a def once rendered (without rendering it). */
export const sfxDuration = (def: SfxDef): number => {
  let d = 0;
  for (const l of def.layers) {
    const p = l.params;
    const v = (i: number, dflt: number): number => p[i] ?? dflt;
    const len = (v(3, 0) || 9 / 44100) + v(18, 0) + v(4, 0) + v(5, 0.1) + v(16, 0);
    d = Math.max(d, (l.at ?? 0) + len);
  }
  return d;
};
